import React, { ReactNode } from 'react';
import { useFormik } from 'formik';
import _ from 'lodash';

import {
  Row,
  Col,
  Button,
  Modal,
  Form,
} from 'react-bootstrap';

import { Spin, Alert } from 'antd';

// Redux
import { useDispatch } from 'react-redux';
import { RootState, AppDispatch, useTypedSelector } from '../../../../../../utils/store.tsx';
import slice, { fields, FormikContext, initialValues, formSchema, prepareForm, prepareData } from './slice.tsx';

import Map from './Map.tsx';

// enums
import { getAddressPlace, validateCoordinates, convertCoordToLatLng, convertLatLngToCoord } from '../../../../../../utils/utils.tsx';
// import {  } from '../../../../../../utils/enums.tsx';

import GoogleSearchAddress from '../../../../../../components/googleMap/GoogleSearchAddress.jsx';

import { ReactComponent as InfoIcon } from "../../../../../../../v5/assets/icons/alert-info.svg";

// ----------------------------------------------------------------------

const FormElement = ({ onCreate, onUpdate, onDelete }: any) => {
  const { isLoading, show, index, id, details } = useTypedSelector((state: RootState) => state.defaultLocationFormSlice);
  const dispatch = useDispatch<AppDispatch>();


  React.useEffect(() => {
    if(show && (id && id > 0)){
      dispatch(slice.callDetailsApi(id, (state: boolean, data: any) => {}));
    } else {
      onCancel();
    }
  }, [id]);

  React.useEffect(() => {
    dispatch(slice.setLoading(true));
    let form = prepareForm(details, initialValues);
    setValues(form);
    dispatch(slice.setLoading(false));
  }, [details]);


  const formik = useFormik({
    initialValues: initialValues,
    validateOnMount: false,
    validateOnChange: false,
    validationSchema: formSchema(null),
    onSubmit: values => {
      onSubmit(values, (data: any) => {
        onCancel();
      });
    },
  });
  const { values, errors, setValues, setFieldValue, validateForm, handleSubmit }: any = formik;


  const onSubmit = (values: any, callback: (data: any) => void) => {
    let data = prepareData(values, id);
    if(id && id > 0){
      dispatch(slice.callUpdateApi(data, (state: boolean, data: any) => {
        if(state){
          if(onUpdate){
            onUpdate(index, data);
          }
          
          callback(data);
        }
      }));
    } else {
      dispatch(slice.callCreateApi(data, (state: boolean, data: any) => {
        if(state){
          if(onCreate){
            onCreate(index, data);
          }

          callback(data);
        }
      }));
    }
  }
  const onCancel = () => {
    let form = prepareForm(null, initialValues);
    setValues(form);
    dispatch(slice.setValues(form));
    dispatch(slice.setShow({ show: false, index: null, id: null }));
    dispatch(slice.resetSlice());
  }


  const setFooter = () => {
    return <Row className='gx-12 w-100'>
      <Col xs={true}>
        {(id && id > 0) && <Button
          variant={'custom-danger'}
          size={'sm'}
          disabled={isLoading}
          onClick={() => {
            dispatch(slice.callDeleteApi({ defaultLocationId: id }, (state: boolean, data: any) => {
              if(state){
                if(onDelete){
                  onDelete(index, id);
                }

                onCancel();
              }
            }));
          }}
        >Delete</Button>}
      </Col>
      <Col xs={'auto'}>
        <Button
          variant={'custom-text'}
          size={'sm'}
          disabled={isLoading}
          onClick={() => {
            onCancel();
          }}
        >Cancel</Button>
      </Col>
      <Col xs={'auto'}>
        <Button
          variant={'custom-primary'}
          size={'sm'}
          disabled={isLoading}
          onClick={() => {
            handleSubmit();
          }}
        >Save</Button>
      </Col>
    </Row>
  }

  const setForm = () => {
    return <Row className={'g-16'}>
      <Col xs={12}>
        <Form.Group>
          <Form.Label>{fields.locationName.label}</Form.Label>
          <Form.Control
            type={'text'}
            autoComplete='off'
            placeholder={fields.locationName.placeholder}
            disabled={false}
            value={values.locationName}
            onChange={(e: any) => {
              setFieldValue('locationName', e.target.value);
            }}
            isInvalid={!!(errors && errors.locationName)}
          />
          <Form.Control.Feedback type="invalid">{errors && errors.locationName as ReactNode}</Form.Control.Feedback>
        </Form.Group>
      </Col>
      <Col xs={12}>
        <Form.Group>
          <Form.Label>{fields.address.label}</Form.Label>
          <Form.Control
            as={GoogleSearchAddress}
            autoComplete='off'
            placeholder={'Insert address or coordinate'}
            isInvalid={!!(errors && errors.address)}
            value={values.addressField}
            onPlaceSelected={async (place: any) => {
              let obj = getAddressPlace(place);

              if (obj?.address && obj?.lat && obj?.lng) {
                await setFieldValue('addressField', obj?.address);
                await setFieldValue('address', obj?.address);
                await setFieldValue('latitude', obj?.lat);
                await setFieldValue('longitude', obj?.lng);
                await setFieldValue('coordinates', obj?.coord);

                await validateForm().then((err: any) => {});
              }
            }}
            onChange={async (e) => {
              const addressField = e.target.value;

              await setFieldValue('addressField', addressField);

              if(addressField != '' && validateCoordinates(addressField)) {
                let coord = convertCoordToLatLng(addressField);
                if (coord) {
                  let coordinates = convertLatLngToCoord(coord.lat, coord.lng);
          
                  // Reset all fields and treat coordinates as a new address
                  await setFieldValue('addressField', addressField);
                  await setFieldValue('address', addressField);
                  await setFieldValue('latitude', coord.lat);
                  await setFieldValue('longitude', coord.lng);
                  await setFieldValue('coordinates', coordinates);

                  await validateForm().then((err: any) => {});
                }
              } else {
                await setFieldValue('address', '');
                await setFieldValue('latitude', '');
                await setFieldValue('longitude', '');
                await setFieldValue('coordinates', '');
              }
            }}
            onKeyDown={async (e: any) => {
              if ((e.charCode || e.keyCode) === 13) {
                e.preventDefault();
                e.stopPropagation();

                const addressField = e.target.value;
                if (validateCoordinates(addressField)) {
                  let coord = convertCoordToLatLng(addressField);
                  if (coord) {
                    let coordinates = convertLatLngToCoord(coord.lat, coord.lng);
            
                    // Reset all fields and treat coordinates as a new address
                    await setFieldValue('addressField', addressField);
                    await setFieldValue('address', addressField);
                    await setFieldValue('latitude', coord.lat);
                    await setFieldValue('longitude', coord.lng);
                    await setFieldValue('coordinates', coordinates);
  
                    await validateForm().then((err: any) => {});
                  }
                }
              }
            }}
            onBlur={async (e) => {
              const addressField = e.target.value;
              const address = values.address;
              const latitude = values.latitude;
              const longitude = values.longitude;
          
              // Rule 1: If addressField is empty, reset all fields
              if (!addressField) {
                await setFieldValue('addressField', '');
                await setFieldValue('address', '');
                await setFieldValue('latitude', '');
                await setFieldValue('longitude', '');
                await setFieldValue('coordinates', '');
                return;
              }
          
              // Rule 2: If addressField is a valid coordinate, treat it as a new address
              if (validateCoordinates(addressField)) {
                let coord = convertCoordToLatLng(addressField);
                if (coord) {
                  let coordinates = convertLatLngToCoord(coord.lat, coord.lng);
          
                  // Reset all fields and treat coordinates as a new address
                  await setFieldValue('addressField', addressField);
                  await setFieldValue('address', addressField);
                  await setFieldValue('latitude', coord.lat);
                  await setFieldValue('longitude', coord.lng);
                  await setFieldValue('coordinates', coordinates);

                  await validateForm().then((err: any) => {});
                }
                return;
              }
          
              // Rule 3: If addressField is manually changed, update address and call API
              if (addressField !== address) {
                let coordinates = convertLatLngToCoord(latitude, longitude);
                if (validateCoordinates(coordinates)) {
                  await setFieldValue('address', addressField); // Update address with the new value
                  await validateForm().then((err: any) => {});
                  return;
                }
              }
          
              // Rule 4: If address is a valid address with coordinates, keep the field as is
              if (address && latitude && longitude) {
                await validateForm().then((err: any) => {});
                return;
              }
          
              // If none of the above conditions are met, reset all fields
              await setFieldValue('addressField', '');
              await setFieldValue('address', '');
              await setFieldValue('latitude', '');
              await setFieldValue('longitude', '');
              await setFieldValue('coordinates', '');
            }}
          />
          <Form.Control.Feedback type="invalid">{errors && errors.address as ReactNode}</Form.Control.Feedback>
        </Form.Group>
      </Col>

      {(values?.coordinates != undefined && values?.coordinates != '') && <>
        <Col xs={12}>
          <Alert
            message=""
            description="You can adjust the position of the marker by dragging it. The coordinates will change but the rest of the fields will stay the same."
            type="info"
            showIcon
            icon={<InfoIcon />}
          />
        </Col>
        <Col xs={12} style={{ minHeight: '280px' }}>
          <Map 
            coordinates={values.coordinates}
            canMoveMarker={true}
            onDragEnd={(position: any) => {
              let coordinates = convertLatLngToCoord(position?.lat, position?.lng);

              setFieldValue('latitude', position?.lat);
              setFieldValue('longitude', position?.lng);
              setFieldValue('coordinates', coordinates);

              const addressField = values?.addressField;
              if (validateCoordinates(addressField)) {
                setFieldValue('addressField', coordinates);
                setFieldValue('address', coordinates);
              }
            }}
          />
        </Col>
      </>}
    </Row>
  }


  return <Modal
    show={show}
    onHide={() => {
      onCancel();
    }}
    backdrop={'static'}
    keyboard={false}
    enforceFocus={false}
    centered={true}
    size={'lg'}
  >
    <FormikContext.Provider value={formik}>
      <Modal.Header closeButton>
        <h6 className='fw-medium'>{((id && id > 0) ? 'Edit' : 'Add')} Default Location</h6>
      </Modal.Header>

      <Spin spinning={isLoading} wrapperClassName={'no-height'}>
        <Modal.Body>{setForm()}</Modal.Body>
      </Spin>

      <Modal.Footer className='px-12'>{setFooter()}</Modal.Footer>
    </FormikContext.Provider>
  </Modal>
}

export default FormElement;
